import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useFirebase } from '../lib/firebase/FirebaseProvider';
import { useAuth } from '../hooks/useAuth';
import { AuthModal } from './auth/AuthModal';
import { FeatureAwareNavigation, defaultNavigationItems } from './FeatureAwareNavigation';
import { GamificationGate } from './GamificationGate';

const Header = () => {
  const { user, userProfile, loading, signOut } = useFirebase();
  const { isAdmin } = useAuth();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalRedirect, setAuthModalRedirect] = useState<string | undefined>(undefined);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Refs for focus management
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  // Handle window resize and reset mobile menu state when crossing md breakpoint (768px)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleResize = () => {
      // Debounce resize events to improve performance
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        // Close mobile menu when crossing to desktop size (768px is Tailwind's md breakpoint)
        if (window.innerWidth >= 768 && showMobileMenu) {
          setShowMobileMenu(false);
        }
      }, 100);
    };

    // Also use matchMedia for more precise breakpoint detection
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleMediaChange = (e: MediaQueryListEvent) => {
      if (e.matches && showMobileMenu) {
        setShowMobileMenu(false);
      }
    };

    window.addEventListener('resize', handleResize);
    mediaQuery.addEventListener('change', handleMediaChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      mediaQuery.removeEventListener('change', handleMediaChange);
      clearTimeout(timeoutId);
    };
  }, [showMobileMenu]);

  // Handle keyboard events and focus trapping for mobile menu
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!showMobileMenu) return;

      if (event.key === 'Escape') {
        setShowMobileMenu(false);
        // Return focus to the mobile menu button
        mobileMenuButtonRef.current?.focus();
        return;
      }

      // Handle Tab key for focus trapping
      if (event.key === 'Tab') {
        const mobileMenu = mobileMenuRef.current;
        if (!mobileMenu) return;

        const focusableElements = mobileMenu.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey) {
          // Shift + Tab (backward)
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else {
          // Tab (forward)
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    if (showMobileMenu) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when mobile menu is open
      document.body.style.overflow = 'hidden';
      
      // Focus the first focusable element in the mobile menu
      setTimeout(() => {
        const mobileMenu = mobileMenuRef.current;
        if (mobileMenu) {
          const firstFocusable = mobileMenu.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          ) as HTMLElement;
          firstFocusable?.focus();
        }
      }, 100); // Small delay to ensure the menu is rendered
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showMobileMenu]);

  // Handle scroll effect for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-[1001] bg-white transition-all duration-200 ${
      isScrolled 
        ? 'shadow-lg border-b border-urban/10' 
        : 'border-b border-urban/5'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-cardboard to-cardboard-600 rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-200 group-hover:scale-105">
                <span className="text-white font-bold text-lg">🏆</span>
              </div>
              <span className="text-2xl font-bold text-urban font-display group-hover:text-cardboard transition-colors duration-200">
                Curbside Quest
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <FeatureAwareNavigation
            items={defaultNavigationItems}
            user={user}
            isAdmin={isAdmin}
            onGuestAction={(action) => {
              if (action === 'AUTH_REQUIRED') {
                setAuthModalRedirect('/submit');
                setShowAuthModal(true);
              }
            }}
            className="hidden md:flex items-center space-x-8"
            itemClassName={(_, isActive) => 
              `text-urban hover:text-cardboard font-medium transition-all duration-200 pb-1 border-b-2 ${
                isActive 
                  ? 'border-cardboard text-cardboard' 
                  : 'border-transparent hover:border-cardboard/50'
              }`
            }
            renderItem={(item, _, defaultContent) => (
              <div key={item.path}>
                {defaultContent}
              </div>
            )}
          />

          {/* Right side - User menu and mobile button */}
          <div className="flex items-center space-x-4">
            {/* Mobile menu button */}
            <button
              ref={mobileMenuButtonRef}
              onClick={toggleMobileMenu}
              className="md:hidden p-2 text-urban hover:text-cardboard transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cardboard focus:ring-offset-2 rounded-md hover:bg-teddy active:scale-95 transform"
              style={{ 
                minWidth: '44px', 
                minHeight: '44px',
              }}
              aria-label={showMobileMenu ? 'Close mobile menu' : 'Open mobile menu'}
              aria-expanded={showMobileMenu}
              aria-controls="mobile-menu"
            >
              <div className="relative w-6 h-6 flex items-center justify-center">
                <div className="absolute inset-0 flex flex-col justify-center space-y-1">
                  <span 
                    className={`block h-0.5 w-6 bg-current transition-all duration-300 ease-out transform origin-center ${
                      showMobileMenu 
                        ? 'rotate-45 translate-y-1.5' 
                        : 'rotate-0 translate-y-0'
                    }`}
                  />
                  <span 
                    className={`block h-0.5 w-6 bg-current transition-all duration-300 ease-out ${
                      showMobileMenu ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
                    }`}
                  />
                  <span 
                    className={`block h-0.5 w-6 bg-current transition-all duration-300 ease-out transform origin-center ${
                      showMobileMenu 
                        ? '-rotate-45 -translate-y-1.5' 
                        : 'rotate-0 translate-y-0'
                    }`}
                  />
                </div>
              </div>
            </button>

            {loading ? (
              <div className="w-10 h-10 bg-teddy rounded-full animate-pulse"></div>
            ) : user ? (
              <div className="flex items-center space-x-4">
                {/* Coin balance display */}
                {userProfile && (
                  <GamificationGate feature="coins">
                    <div className="hidden sm:flex items-center space-x-3 bg-gradient-to-r from-treasure/20 to-treasure/10 px-3 py-2 rounded-full border border-treasure/20">
                      <div className="flex items-center gap-1 text-sm font-semibold">
                        <span className="text-treasure">🪙</span>
                        <span className="text-urban">{userProfile.goldCoins}</span>
                      </div>
                      <div className="w-px h-4 bg-treasure/30"></div>
                      <div className="flex items-center gap-1 text-sm font-semibold">
                        <span className="text-cardboard">🎫</span>
                        <span className="text-urban">{userProfile.farAwayTokens}</span>
                      </div>
                    </div>
                  </GamificationGate>
                )}
                
                {/* User profile dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 p-2 text-urban hover:text-cardboard focus:outline-none focus:ring-2 focus:ring-cardboard focus:ring-offset-2 rounded-full hover:bg-teddy transition-all duration-200"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-cardboard to-cardboard-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {(userProfile?.displayName || user.displayName || user.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-lg py-2 z-[1002] border border-urban/10">
                      {/* User info header */}
                      <div className="px-4 py-3 border-b border-urban/10">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-cardboard to-cardboard-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {(userProfile?.displayName || user.displayName || user.email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-urban text-sm">
                              {userProfile?.displayName || user.displayName || user.email}
                            </div>
                            {userProfile && (
                              <GamificationGate feature="coins">
                                <div className="flex items-center space-x-3 mt-1">
                                  <span className="flex items-center gap-1 text-xs text-urban/70">
                                    <span className="text-treasure">🪙</span>
                                    {userProfile.goldCoins} coins
                                  </span>
                                  <span className="flex items-center gap-1 text-xs text-urban/70">
                                    <span className="text-cardboard">🎫</span>
                                    {userProfile.farAwayTokens} tokens
                                  </span>
                                </div>
                              </GamificationGate>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Menu items */}
                      <div className="py-1">
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-2 text-sm text-urban hover:bg-teddy hover:text-cardboard transition-colors"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <span className="mr-3">👤</span>
                          Profile
                        </Link>
                        {isAdmin && (
                          <Link
                            to="/admin"
                            className="flex items-center px-4 py-2 text-sm text-urban hover:bg-teddy hover:text-cardboard transition-colors"
                            onClick={() => setShowUserMenu(false)}
                          >
                            <span className="mr-3">🛡️</span>
                            Admin Dashboard
                          </Link>
                        )}
                        <Link
                          to="/settings"
                          className="flex items-center px-4 py-2 text-sm text-urban hover:bg-teddy hover:text-cardboard transition-colors"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <span className="mr-3">⚙️</span>
                          Settings
                        </Link>
                        <div className="border-t border-urban/10 my-1"></div>
                        <button
                          onClick={handleSignOut}
                          className="flex items-center w-full px-4 py-2 text-sm text-urban hover:bg-rust/10 hover:text-rust transition-colors"
                        >
                          <span className="mr-3">🚪</span>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <button 
                onClick={() => {
                  setAuthModalRedirect(undefined);
                  setShowAuthModal(true);
                }}
                className="bg-cardboard hover:bg-cardboard-600 text-white font-semibold px-6 py-2 rounded-full transition-all duration-200 hover:shadow-md active:scale-95"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>
    
      {/* Mobile Navigation Overlay */}
      <div 
        className={`fixed inset-0 z-[1002] md:hidden transition-all duration-300 ease-in-out ${
          showMobileMenu 
            ? 'opacity-100 pointer-events-auto' 
            : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 bg-black/60 transition-all duration-300 ease-out ${
            showMobileMenu ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setShowMobileMenu(false)}
          aria-hidden="true"
          style={{
            backdropFilter: showMobileMenu ? 'blur(8px)' : 'blur(0px)',
            WebkitBackdropFilter: showMobileMenu ? 'blur(8px)' : 'blur(0px)',
          }}
        />
        
        {/* Mobile menu panel */}
        <div 
          ref={mobileMenuRef}
          id="mobile-menu"
          className={`fixed right-0 top-0 h-full w-80 max-w-full bg-white shadow-2xl transition-all duration-300 ${
            showMobileMenu 
              ? 'translate-x-0 opacity-100' 
              : 'translate-x-full opacity-0'
          }`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-menu-title"
        >
          <div className="flex flex-col h-full">
            {/* Mobile menu header */}
            <div className="flex items-center justify-between p-4 border-b border-urban/10">
              <div>
                <h2 id="mobile-menu-title" className="text-lg font-semibold text-urban">Menu</h2>
              </div>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="p-2 text-urban hover:text-cardboard hover:bg-teddy transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cardboard focus:ring-offset-2 rounded-md"
                style={{ 
                  minWidth: '44px', 
                  minHeight: '44px',
                }}
                aria-label="Close navigation menu"
              >
                <svg 
                  className="w-6 h-6" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Mobile menu content */}
            <div className="flex-1 overflow-y-auto">
              {/* User Profile Section */}
              {user ? (
                <div className="p-4 border-b border-urban/10">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-cardboard to-cardboard-600 rounded-full flex items-center justify-center text-white font-bold">
                      {(userProfile?.displayName || user.displayName || user.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="text-urban font-semibold text-sm">
                        {userProfile?.displayName || user.displayName || user.email}
                      </div>
                      {userProfile && (
                        <GamificationGate feature="coins">
                          <div className="flex items-center space-x-3 mt-1">
                            <span className="flex items-center gap-1 text-xs text-urban/70">
                              <span className="text-treasure">🪙</span>
                              {userProfile.goldCoins} coins
                            </span>
                            <span className="flex items-center gap-1 text-xs text-urban/70">
                              <span className="text-cardboard">🎫</span>
                              {userProfile.farAwayTokens} tokens
                            </span>
                          </div>
                        </GamificationGate>
                      )}
                    </div>
                  </div>
                  
                  {/* User Actions */}
                  <div className="space-y-1">
                    <Link
                      to="/profile"
                      className="flex items-center px-3 py-2 text-urban hover:text-cardboard hover:bg-teddy rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cardboard focus:ring-offset-2"
                      style={{ minHeight: '44px' }}
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span className="mr-3">👤</span>
                      Profile
                    </Link>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        className="flex items-center px-3 py-2 text-urban hover:text-cardboard hover:bg-teddy rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cardboard focus:ring-offset-2"
                        style={{ minHeight: '44px' }}
                        onClick={() => setShowMobileMenu(false)}
                      >
                        <span className="mr-3">🛡️</span>
                        Admin Dashboard
                      </Link>
                    )}
                    <Link
                      to="/settings"
                      className="flex items-center px-3 py-2 text-urban hover:text-cardboard hover:bg-teddy rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cardboard focus:ring-offset-2"
                      style={{ minHeight: '44px' }}
                      onClick={() => setShowMobileMenu(false)}
                    >
                      <span className="mr-3">⚙️</span>
                      Settings
                    </Link>
                    <button
                      onClick={async () => {
                        await handleSignOut();
                        setShowMobileMenu(false);
                      }}
                      className="flex items-center w-full px-3 py-2 text-urban hover:text-rust hover:bg-rust/10 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rust focus:ring-offset-2"
                      style={{ minHeight: '44px' }}
                    >
                      <span className="mr-3">🚪</span>
                      Sign Out
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 border-b border-urban/10">
                  <button 
                    onClick={() => {
                      setAuthModalRedirect(undefined);
                      setShowAuthModal(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full bg-cardboard hover:bg-cardboard-600 text-white font-semibold px-4 py-3 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cardboard focus:ring-offset-2 hover:shadow-md active:scale-95"
                    style={{ minHeight: '44px' }}
                  >
                    <span className="mr-2">🔐</span>
                    Sign In
                  </button>
                </div>
              )}

              <div className="p-4">
                {/* Navigation Links */}
                <FeatureAwareNavigation
                  items={defaultNavigationItems}
                  user={user}
                  isAdmin={isAdmin}
                  onItemClick={() => setShowMobileMenu(false)}
                  onGuestAction={(action) => {
                    if (action === 'AUTH_REQUIRED') {
                      setAuthModalRedirect('/submit');
                      setShowAuthModal(true);
                      setShowMobileMenu(false);
                    }
                  }}
                  className="space-y-2"
                  itemClassName={(_, isActive) => 
                    `flex items-center px-4 py-3 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cardboard focus:ring-offset-2 ${
                      isActive 
                        ? 'text-cardboard bg-cardboard/10 border-l-4 border-cardboard' 
                        : 'text-urban hover:text-cardboard hover:bg-teddy'
                    }`
                  }
                  renderItem={(item, _, defaultContent) => (
                    <div key={item.path} style={{ minHeight: '44px' }}>
                      {defaultContent}
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setAuthModalRedirect(undefined);
        }}
        initialMode="login"
        redirectTo={authModalRedirect}
      />
    </header>
  );
};

export default Header;